import { Avatar, Box, Breadcrumbs, Hidden, IconButton, Skeleton, Typography } from '@mui/material';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { getBreadcrumbs, getInitials } from '../utils/string';
import { Trans, useTranslation } from 'react-i18next';
import BasicToggleButtonGroup from './common/BasicToggleButtonGroup';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useContext, useEffect } from 'react';
import { formatNumberWithComma } from '../utils/number';
import { UserContext } from '../contexts/UserContext';
import MenuIcon from '@mui/icons-material/Menu';
import { useSidebar } from '../contexts/SidebarContext';
import IResponse from '../interfaces/response';
import axios from 'axios';
import { hideBreadCrumbsItem } from '../constants/breadCrumbs';

function Header() {
	const { t } = useTranslation();
	const { userBalance, setUserBalance, user } = useContext(UserContext);
	const location = useLocation();
	const breadCrumbs = getBreadcrumbs(location.pathname);
	const navigate = useNavigate();

	const balance = formatNumberWithComma(userBalance);
	const { setSidebarOpen } = useSidebar();

	const isAdmin = user?.is_admin;
	const handleOpenSidebar = () => {
		setSidebarOpen(prev => !prev);
	};

	useEffect(() => {
		axios<IResponse<number>>({
			url: '/accounting-profile/user-balance',
		}).then(res => {
			if (res.data.success) {
				setUserBalance(res.data.data);
			}
		});
	}, []);

	const goToPayment = () => {
		navigate('/finance/add-balance');
	};

	return (
		<Box
			className="sticky top-0 z-40 mb-0 mt-4 flex min-h-max flex-row items-center justify-between gap-y-5 rounded-md bg-background-paper px-4 py-3 md:mb-6 lg:min-h-20 lg:py-0"
			sx={{
				boxShadow: 3,
			}}
		>
			<Hidden smDown>
				<Breadcrumbs aria-label="breadcrumb">
					<Link className="mr-1" to="/">
						{t('home')}
					</Link>
					{breadCrumbs.map((breadCrumb, i) => {
						const isLastItem = breadCrumb === breadCrumbs[breadCrumbs.length - 1];
						const filterBreadCrumbs = hideBreadCrumbsItem.find(item => item.title === breadCrumb);
						const path = `/${breadCrumbs.slice(0, i + 1).join('/')}`;
						if (!Number(breadCrumb)) {
							return !filterBreadCrumbs ? (
								<Link key={i} className={isLastItem ? 'text.primary' : ''} to={`${path}`} aria-current="page">
									{t(breadCrumb)}
								</Link>
							) : filterBreadCrumbs?.route ? (
								<Link key={i} className={isLastItem ? 'text.primary' : ''} to={`${filterBreadCrumbs?.route}`} aria-current="page">
									{t(breadCrumb)}
								</Link>
							) : (
								<Typography className={`${filterBreadCrumbs?.hide ? 'hidden' : 'block'}`}>{t(breadCrumb)}</Typography>
							);
						}
					})}
				</Breadcrumbs>
			</Hidden>
			<Hidden smUp>
				<IconButton onClick={handleOpenSidebar}>
					<MenuIcon />
				</IconButton>
			</Hidden>
			{isAdmin ? (
				<Box className="flex items-center gap-6">
					<IconButton className="p-0">
						<NotificationsNoneOutlinedIcon className="h-7 w-7 text-text-primary" />
					</IconButton>
					<div className="relative flex items-center">
						<span className="absolute bottom-1 right-0.5 z-50 h-2 w-2 rounded-full bg-success-main" />
						<Avatar
							alt="avatar"
							src={
								user && user.image
									? typeof user.image === 'string'
										? user.image // image is a URL string, use it directly
										: URL.createObjectURL(user.image) // image is a File or Blob, create object URL
									: ''
							}
							className="ml-2 h-10 w-10 border bg-gray-100"
						>
							{getInitials(user?.name!)}
						</Avatar>
					</div>
				</Box>
			) : (
				<Box>
					<IconButton component={Link} to="/finance/cart" className="h-10 w-10 p-0">
						<ShoppingCartOutlinedIcon fontSize="large" className="text-primary-main" />
					</IconButton>
					<Hidden mdDown>
						<BasicToggleButtonGroup handleToggle={goToPayment} className="ms-5 h-10 min-w-64 border-primary-main/15 lg:h-auto" activeButton={1} activeButtonClasses={'grow rounded cursor-pointer bg-primary-main/15'} nonActiveButtonClasses="text-primary-main grow bg-background-paper cursor-default" titles={[(userBalance === undefined ? <Skeleton width={120} /> : <Trans i18nKey="current_balance_label" values={{ balance }} components={[<Typography className="text-sm font-medium text-text-primary" />]} />) as any, t('increase_balance')]} />
					</Hidden>
				</Box>
			)}
		</Box>
	);
}
export default Header;
